import { useCallback, useEffect, useMemo, useState } from 'react';

import { getApiBaseUrl } from '../utils/apiConfig';
import { recordSkinFlowStage } from '../utils/dataSaver';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { getSkinFlowState, saveSkinFlowState } from '../utils/skinFlowState';

function restoreCartFromApiItems(cartAdded) {
  if (!Array.isArray(cartAdded) || cartAdded.length === 0) return null;
  const restored = {};
  cartAdded.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    const product = item.product;
    if (!product || typeof product !== 'object') return;
    const quantity = Number(item.quantity) || 0;
    if (quantity <= 0) return;
    const productId = product.product_id || product.id || product.sku || `cart_${idx}`;
    restored[productId] = { product, quantity };
  });
  return Object.keys(restored).length > 0 ? restored : null;
}

/**
 * Cart + checkout state — mirrors SkinRoutinePage (persistence, API, checkout modal).
 */
export function useSkinProductCart({
  sessionId,
  appSessionToken,
  pageName = 'SkinResultPage',
  customerDetails = {},
}) {
  const apiBaseUrl = useMemo(
    () => (getApiBaseUrl() || process.env.REACT_APP_API_URL || '').replace(/\/$/, ''),
    [],
  );

  const [cartItems, setCartItems] = useState(() => {
    if (!sessionId) return {};
    const stored = getSkinFlowState(sessionId);
    return stored?.cartItems && typeof stored.cartItems === 'object' ? stored.cartItems : {};
  });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    saveSkinFlowState(sessionId, { ...current, sessionId, cartItems });
  }, [sessionId, cartItems]);

  useEffect(() => {
    if (!sessionId || !apiBaseUrl) return undefined;
    let cancelled = false;

    const params = new URLSearchParams();
    if (appSessionToken) params.set('app_session_token', appSessionToken);
    const qs = params.toString() ? `?${params.toString()}` : '';

    fetchWithTimeout(`${apiBaseUrl}/api/get-stored-products/${sessionId}${qs}`, { timeoutMs: 20000 })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const restored = restoreCartFromApiItems(data?.cart_added);
        if (!restored) return;
        setCartItems((prev) => (
          prev && Object.keys(prev).length > 0 ? prev : restored
        ));
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sessionId, appSessionToken, apiBaseUrl]);

  const handleAddToCart = useCallback((product, quantity, productId) => {
    const prevQty = cartItems[productId]?.quantity || 0;
    if (prevQty === 0 && quantity > 0 && sessionId) {
      recordSkinFlowStage(
        sessionId,
        'add_to_cart',
        appSessionToken || null,
        { page_name: pageName, quantity },
      ).catch(() => {});
    }
    setCartItems((prev) => {
      if (quantity === 0) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { product, quantity } };
    });
  }, [appSessionToken, cartItems, pageName, sessionId]);

  const getTotalCartItems = useCallback(
    () => Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cartItems],
  );

  const handleCheckout = useCallback(() => {
    if (isCheckoutModalOpen) return;

    const cartProducts = Object.entries(cartItems)
      .filter(([, item]) => item.quantity > 0)
      .map(([productId, item]) => ({ ...item, productId }));

    if (cartProducts.length === 0) {
      alert('Your cart is empty. Please add products to cart first.');
      return;
    }

    recordSkinFlowStage(
      sessionId,
      'checkout_started',
      appSessionToken || null,
      {
        page_name: pageName,
        quantity: cartProducts.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      },
    ).catch(() => {});

    setIsCheckoutModalOpen(true);
  }, [appSessionToken, cartItems, isCheckoutModalOpen, pageName, sessionId]);

  const handleCloseCheckoutModal = useCallback(() => setIsCheckoutModalOpen(false), []);

  const handleCartUpdate = useCallback((updatedCartItems) => setCartItems(updatedCartItems), []);

  const handleViewProduct = useCallback((product, productUrl) => {
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;

    if (sessionId) {
      recordSkinFlowStage(sessionId, 'product_viewed', appSessionToken || null, {
        page_name: pageName,
      }).catch(() => {});
    }

    if (apiBaseUrl && sessionId && productId) {
      fetchWithTimeout(`${apiBaseUrl}/api/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(appSessionToken && { app_session_token: appSessionToken }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }

    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [apiBaseUrl, appSessionToken, pageName, sessionId]);

  const saveCartToBackend = useCallback((items) => {
    if (!apiBaseUrl || !sessionId) return;
    const cartProducts = Object.entries(items)
      .filter(([, item]) => item && item.quantity > 0)
      .map(([productId, item]) => ({
        id: Number(productId) || item.product?.id || item.product?.product_id,
        quantity: item.quantity,
      }));

    fetchWithTimeout(`${apiBaseUrl}/api/save-cart-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skin_id: sessionId,
        session_id: sessionId,
        ...(appSessionToken && { app_session_token: appSessionToken }),
        cart_products: cartProducts,
      }),
      timeoutMs: 20000,
    }).then((r) => {
      if (!r.ok) console.warn('Could not save cart to backend');
    }).catch(() => {});
  }, [apiBaseUrl, appSessionToken, sessionId]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  const checkoutCartItems = useMemo(
    () => Object.entries(cartItems)
      .filter(([, item]) => item.quantity > 0)
      .map(([productId, item]) => ({ ...item, productId })),
    [cartItems],
  );

  return {
    cartItems,
    checkoutCartItems,
    isCheckoutModalOpen,
    handleAddToCart,
    handleCheckout,
    handleCloseCheckoutModal,
    handleCartUpdate,
    handleViewProduct,
    getTotalCartItems,
    apiBaseUrl,
    customerDetails,
  };
}

export default useSkinProductCart;
